import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export default function RebateConfig() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [rebateConfigs, setRebateConfigs] = useState([])
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [notification, setNotification] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    utility_provider: '',
    region: '',
    climate_zone: '4A',
    cooling_hours: 1400,
    heating_hours: 2200,
    typical_system_life: 12,
    gas_price_per_therm: 1.20,
    valid_from: '',
    valid_until: '',
    is_active: true,
  })

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      navigate('/dashboard')
      return
    }

    if (user) {
      fetchRebateConfigs()
    }
  }, [user, navigate])

  const fetchRebateConfigs = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('rebate_configs')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setRebateConfigs(data || [])
    } catch (error) {
      console.error('Error fetching rebate configs:', error)
      showNotification('Error fetching rebate configurations', 'error')
    } finally {
      setLoading(false)
    }
  }

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 5000)
  }

  const handleEdit = (config) => {
    setSelectedConfig(config)
    setFormData({
      name: config.name,
      utility_provider: config.utility_provider,
      region: config.region,
      climate_zone: config.climate_zone,
      cooling_hours: config.cooling_hours,
      heating_hours: config.heating_hours,
      typical_system_life: config.typical_system_life,
      gas_price_per_therm: config.gas_price_per_therm,
      valid_from: config.valid_from,
      valid_until: config.valid_until,
      is_active: config.is_active,
    })
    setIsEditing(true)
  }

  const handleCreateNew = () => {
    setSelectedConfig(null)
    setFormData({
      name: '',
      utility_provider: '',
      region: '',
      climate_zone: '4A',
      cooling_hours: 1400,
      heating_hours: 2200,
      typical_system_life: 12,
      gas_price_per_therm: 1.20,
      valid_from: '',
      valid_until: '',
      is_active: true,
    })
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setSelectedConfig(null)
  }

  const handleSave = async () => {
    try {
      if (selectedConfig) {
        // Update existing
        const { error } = await supabase
          .from('rebate_configs')
          .update(formData)
          .eq('id', selectedConfig.id)

        if (error) throw error
        showNotification('Rebate configuration updated successfully')
      } else {
        // Create new
        const { error } = await supabase
          .from('rebate_configs')
          .insert([{ ...formData, created_by: user.id }])

        if (error) throw error
        showNotification('Rebate configuration created successfully')
      }

      setIsEditing(false)
      setSelectedConfig(null)
      fetchRebateConfigs()
    } catch (error) {
      console.error('Error saving rebate config:', error)
      showNotification('Error saving rebate configuration', 'error')
    }
  }

  const handleToggleActive = async (config) => {
    try {
      const { error } = await supabase
        .from('rebate_configs')
        .update({ is_active: !config.is_active })
        .eq('id', config.id)

      if (error) throw error
      showNotification(`Rebate configuration ${!config.is_active ? 'activated' : 'deactivated'}`)
      fetchRebateConfigs()
    } catch (error) {
      console.error('Error toggling active status:', error)
      showNotification('Error updating configuration', 'error')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-xl text-gray-600">Loading rebate configurations...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Rebate Configuration</h1>
          <p className="text-gray-600">Manage regional utility rebate programs and incentives</p>
          <div className="mt-4 flex gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
            >
              ← Back to Admin
            </button>
            {!isEditing && (
              <button
                onClick={handleCreateNew}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white rounded-lg transition"
              >
                + Create New Configuration
              </button>
            )}
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {notification.message}
          </div>
        )}

        {/* Edit Form */}
        {isEditing && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {selectedConfig ? 'Edit Rebate Configuration' : 'Create New Rebate Configuration'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Configuration Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Duke Energy Smart $aver 2025"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Utility Provider *
                </label>
                <input
                  type="text"
                  value={formData.utility_provider}
                  onChange={(e) => setFormData({ ...formData, utility_provider: e.target.value })}
                  placeholder="e.g., Duke Energy"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Region
                </label>
                <input
                  type="text"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  placeholder="e.g., Triad NC"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Climate Zone
                </label>
                <select
                  value={formData.climate_zone}
                  onChange={(e) => setFormData({ ...formData, climate_zone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="1A">1A - Very Hot, Humid</option>
                  <option value="2A">2A - Hot, Humid</option>
                  <option value="3A">3A - Warm, Humid</option>
                  <option value="4A">4A - Mixed, Humid</option>
                  <option value="5A">5A - Cool, Humid</option>
                  <option value="6A">6A - Cold, Humid</option>
                  <option value="7">7 - Very Cold</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cooling Hours (annual)
                </label>
                <input
                  type="number"
                  value={formData.cooling_hours}
                  onChange={(e) => setFormData({ ...formData, cooling_hours: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heating Hours (annual)
                </label>
                <input
                  type="number"
                  value={formData.heating_hours}
                  onChange={(e) => setFormData({ ...formData, heating_hours: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Typical System Life (years)
                </label>
                <input
                  type="number"
                  value={formData.typical_system_life}
                  onChange={(e) => setFormData({ ...formData, typical_system_life: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gas Price per Therm ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.gas_price_per_therm}
                  onChange={(e) => setFormData({ ...formData, gas_price_per_therm: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valid From
                </label>
                <input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valid Until
                </label>
                <input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Active (available to contractors)
                </label>
              </div>
            </div>

            <div className="mt-6 flex gap-4">
              <button
                onClick={handleSave}
                disabled={!formData.name || !formData.utility_provider}
                className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedConfig ? 'Update Configuration' : 'Create Configuration'}
              </button>
              <button
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Configurations List */}
        {!isEditing && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Configuration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Region
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Climate Zone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valid Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rebateConfigs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No rebate configurations found. Click "Create New Configuration" to get started.
                      </td>
                    </tr>
                  ) : (
                    rebateConfigs.map((config) => (
                      <tr key={config.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{config.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {config.utility_provider}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {config.region || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {config.climate_zone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(config.valid_from)} - {formatDate(config.valid_until)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {config.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4">
                          <button
                            onClick={() => handleEdit(config)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleActive(config)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            {config.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info Card */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">Rebate Configuration Notes</h3>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Rebate configurations are assigned to contractors during white-glove onboarding</li>
            <li>• Only active configurations are available for selection</li>
            <li>• Climate zone determines cooling/heating hours for energy calculations</li>
            <li>• Rebate tiers (AC, Heat Pump, Conversion) are managed in the database directly</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
